import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fToNow } from 'src/utils/format-time';

import { NotificationTypes } from 'src/models/notification';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import { useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { Role } from 'src/models/user';

type Recipient = {
  recipientId: string;
  recipientType: string;
  isRead: boolean;
  isArchived?: boolean;
};

type NotificationItemProps = {
  notification: {
    _id: string;
    message: string;
    title: string;
    createdAt: Date;
    notificationType: string;
    status: string;
    recipients: Recipient[];
    referenceId?: string;
    docType?: string;
  };
  currentRecipient: Recipient;
  handleMarkAsRead: (id: string) => void;
  handleArchive?: (id: string) => void;
  handleUnarchive?: (id: string) => void;
};

export default function NotificationItem({
  notification,
  currentRecipient,
  handleMarkAsRead,
  handleArchive,
  handleUnarchive,
}: NotificationItemProps) {
  const router = useRouter();
  const { user } = useAuthContext();

  const [isCurrentRecipientUnread, setIsCurrentRecipientUnread] = useState<boolean>(
    currentRecipient ? !currentRecipient.isRead : true
  );

  const handlePageRoute = (
    notificationType: string,
    referenceId: string | null,
    docType: string,
    id: string
  ) => {
    handleMarkAsRead(id);
    setIsCurrentRecipientUnread(false);

    switch (notificationType) {
      case NotificationTypes.SKILL_ADDED:
      case NotificationTypes.ASSIGN_NEW_ASSIGNMENT:
      case NotificationTypes.CHECKLIST_COMPLETED:
        if (referenceId && docType) {
          const parts = docType.split(':');
          const userId = parts[1];
          const isAdminOrManager = user?.role === Role.ADMIN || user?.role === Role.MANAGER;

          if (isAdminOrManager && userId) {
            router.push({ pathname: paths.user.performance(userId) });
          } else {
            router.push({ pathname: `/skills/${referenceId}` });
          }
        }
        break;
      case NotificationTypes.GENERAL:
        if (docType === 'Article' && referenceId) {
          router.push({
            pathname: `/articles/${referenceId}`,
          });
        } else {
          router.push({ pathname: paths.dashboard.root });
        }
        break;
      default:
        router.push({
          pathname: paths.dashboard.root,
        });
        break;
    }
  };

  const renderIcon = (
    <ListItemAvatar>
      <Stack alignItems="center" justifyContent="center">
        <Iconify
          icon={
            notification.notificationType === NotificationTypes.SKILL_ADDED ||
            notification.notificationType === NotificationTypes.ASSIGN_NEW_ASSIGNMENT
              ? 'fluent-color:clipboard-task-16'
              : notification.notificationType === NotificationTypes.CHECKLIST_COMPLETED
                ? 'solar:medal-star-bold-duotone'
                : 'solar:document-bold-duotone'
          }
          width={40}
          height={40}
        />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={reader(notification.title ?? '')}
      secondary={
        <>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: 'body2', color: 'text.primary' }}
            divider={
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  bgcolor: 'currentColor',
                  mx: 0.5,
                  borderRadius: '50%',
                }}
              />
            }
          >
            {notification.message}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: 'caption', color: 'text.disabled' }}
            divider={
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  bgcolor: 'currentColor',
                  mx: 0.5,
                  borderRadius: '50%',
                }}
              />
            }
          >
            {fToNow(notification.createdAt)}
          </Stack>
        </>
      }
    />
  );

  const renderUnReadBadge = isCurrentRecipientUnread && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
        cursor: 'pointer',
      }}
      onClick={() =>
        handlePageRoute(
          notification.notificationType,
          notification.referenceId ?? null,
          notification.docType ?? 'module',
          notification._id
        )
      }
    />
  );

  const renderAction = (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() =>
          handlePageRoute(
            notification.notificationType,
            notification.referenceId ?? null,
            notification.docType ?? 'module',
            notification._id
          )
        }
      >
        View
      </Button>

      {!currentRecipient?.isArchived && handleArchive && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => handleArchive(notification._id)}
        >
          Archive
        </Button>
      )}

      {currentRecipient?.isArchived && handleUnarchive && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => handleUnarchive(notification._id)}
        >
          Unarchive
        </Button>
      )}
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
      }}
    >
      {renderUnReadBadge}

      {renderIcon}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {renderAction}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
